import axios from "axios";
import isRelevantBrowserError from "common/utils/app-monitoring/is-relevant-browser-error";
import { AXIOS_NETWORK_PATTERNS } from "common/utils/app-monitoring/network-error-patterns";

// Infrastructure gateway errors — not actionable from app code.
// 502/503/504 are returned by Envoy/nginx during pod scaling and deploys.
// Mirror of IGNORED_STATUSES in is-relevant-browser-error.js.
const IGNORED_STATUSES = new Set([429, 499, 502, 503, 504]);

export const isAxiosNetworkError = error => {
  if (
    error?.code === "ERR_NETWORK" ||
    error?.code === "ERR_TIMEOUT" ||
    error?.code === "ECONNABORTED"
  )
    return true;
  const msg = error?.message || "";
  if (msg === "Unable to connect to server") return true; // sentinel from our own catch handler
  return AXIOS_NETWORK_PATTERNS.some(pattern => pattern.test(msg));
};

export const shouldReportError = error => {
  if (!error) return true;

  // fetch errors (abort) and infrastructure gateway noise (429, 499, 502, 503, 504)
  if (error.name === "AbortError") return false;
  if (IGNORED_STATUSES.has(error.status)) return false;

  // axios errors (http + cancel)
  if (axios.isAxiosError?.(error)) {
    if (error.response?.status === 499) return false;
    if (error.code === "ERR_CANCELED" || error.message === "canceled") return false;

    // legacy cancel token / compat
    if (axios.isCancel?.(error)) return false;
  }

  // fallback for existing checks / non-axios cancel shapes
  if (error.name === "CanceledError") return false;
  if (error.message === "Client Closed Request") return false;

  // Only suppress as network error when there is no HTTP status — prevents
  // suppressing real server errors whose message happens to match a broad
  // pattern (e.g., a 503 body containing "offline").
  const httpStatus = error.status ?? error.response?.status;
  if (!httpStatus && isAxiosNetworkError(error)) return false;

  // Infrastructure noise (WAF blocks, gateway errors, rate limits) —
  // overlaps with some checks above; overlap is intentional and harmless.
  if (!isRelevantBrowserError(error)) return false;

  return true;
};

// MN-8803: classify a no-status network error into a low-cardinality sub-type so
// the "Unable to connect to server" cluster can be split (failed-to-fetch vs
// load-failed vs timeout vs reset vs refused vs dns vs aborted). Self-contained
// (does not depend on AXIOS_NETWORK_PATTERNS ordering). First match wins.
const NETWORK_SUBTYPE_RULES = [
  [
    "timeout",
    e =>
      e?.code === "ERR_TIMEOUT" ||
      e?.code === "ECONNABORTED" ||
      e?.code === "ETIMEDOUT" ||
      /timeout|ETIMEDOUT/i.test(e?.message || "")
  ],
  ["conn_reset", e => /ECONNRESET|connection\s*reset/i.test(e?.message || "")],
  ["conn_refused", e => /ECONNREFUSED/i.test(e?.message || "")],
  ["dns", e => /ENOTFOUND/i.test(e?.message || "")],
  [
    "aborted",
    e => e?.name === "AbortError" || /request\s*aborted|AbortError/i.test(e?.message || "")
  ],
  ["load_failed", e => /Load failed/i.test(e?.message || "")],
  [
    "failed_to_fetch",
    e =>
      e?.code === "ERR_NETWORK" ||
      // Generic network-failure strings across vendors (axios "Network Error",
      // Firefox "NetworkError", Safari "...internet connection appears to be
      // offline") — bucket with failed_to_fetch so they aren't lost to "unknown".
      /Failed to fetch|net::ERR_|network\s*error|internet connection/i.test(e?.message || "")
  ]
];

export const classifyNetworkSubType = error => {
  const rule = NETWORK_SUBTYPE_RULES.find(([, test]) => test(error));
  return rule ? rule[0] : "unknown";
};
